import {
  Alert,
  Box,
  CardActions,
  CircularProgress,
  Grid,
  Pagination,
  Typography,
} from '@mui/material';
import {
  ChangeEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { SearchProvider, useSearch, useSiteContext } from 'src/store';
import { SearchResultsContainer, SearchResultsHeader } from 'src/pages/search';

import AppCard from 'src/components/AppCard';
import AppliedFilters from 'src/components/Search/AppliedFilters';
import SearchControls from 'src/components/Search/SearchControls';
import { CardList } from '@orcha/micro-library-core-ui';
import { SortBy } from '@orcha/micro-library-search-ui';
import { DynamicBreadcrumbs } from 'src/components/Breadcrumbs';
import Filters from 'src/components/Search/Filters';
import { PageContentContainer } from 'src/components/common/styles/PageLayout';
import RootWidgetLayout from 'src/components/layouts/RootWidgetLayout';
import { SearchSortByEnum } from '@orcha/types';
import { Translate } from 'next-translate';
import { enumFromStringValue } from '@orcha/utils';
import useStorage from 'src/hooks/useStorage';
import theme from 'src/themes';
import { useRouter } from 'next/router';
import useSearchData from 'src/hooks/useSearchData';
import useSortByOptions from 'src/hooks/useSortByOptions';
import useTranslation from 'next-translate/useTranslation';
import { TopicPageProps } from './TopicPage.types';
import Error from '../../../pages/_error';

export const TopicPage = (props: TopicPageProps) => {
  const {
    pageName,
    configuration: { title, subtitle, appContainerId, displaySearchBar },
    urlSlug,
    fullUrlSlug,
    queryParams,
    id,
    breadcrumbsCount,
  } = props;
  const pageTitle = pageName && pageName !== '' ? pageName : title;
  const { t } = useTranslation();
  const breadcrumbText = pageTitle;
  useStorage().setItem('topicPageBreadcrumb', { breadcrumbText }, 'session');

  const router = useRouter();
  const { locale } = router;
  const { countryOfUse } = useSiteContext();
  const { appliedFilters, sortBy, setSortBy, currentPage, setCurrentPage } =
    useSearch();
  const sortByOptions = useSortByOptions();

  const PAGE_SIZE = 12;

  const filterQuery = appliedFilters
    ?.filter((f) => f.selectedOptions.length > 0)
    .map((filter) => {
      return filter.selectedOptions
        .map((s) => `&${filter.filterName}=${encodeURIComponent(s)}`)
        .join('');
    })
    .join('');

  const { searchTerm } = useSearch();
  const [localSearchTerm, setLocalSearchTerm] = useState<string | undefined>(
    searchTerm
  );

  const filterQueryString = filterQuery ? `${filterQuery}` : '';
  const localeQueryString = locale ? `&language=${locale}` : '';
  const sortByQueryString = sortBy ? `&sortBy=${sortBy}` : '';
  const countryQueryString = countryOfUse
    ? `&country=${countryOfUse.code}`
    : '';

  const searchTermQueryString = localSearchTerm
    ? `&searchTerm=${encodeURIComponent(localSearchTerm)}`
    : '';
  const { searchResults, error, isLoading } = useSearchData(
    currentPage,
    PAGE_SIZE,
    appContainerId,
    `${filterQueryString}${localeQueryString}${sortByQueryString}${countryQueryString}${searchTermQueryString}`
  );

  const totalPageCount = searchResults?.paging?.totalPages ?? 0;
  const appCount = searchResults?.paging?.totalItems ?? 0;

  const handleSearchClick = () => {
    if (searchTerm === localSearchTerm) return;
    setLocalSearchTerm(searchTerm);
    setCurrentPage(1);
  };

  const updatePageURL = useCallback(() => {
    const otherQuery = queryParams
      ? Object.keys(queryParams)
          .map((key) => {
            const value = queryParams[key];

            if (Array.isArray(value)) {
              return value
                .map((v) => `&${key}=${encodeURIComponent(v)}`)
                .join('');
            }

            return `&${key}=${encodeURIComponent(value)}`;
          })
          .join('')
      : '';

    const sortByQuery = sortBy ? `&sortBy=${sortBy}` : '';
    const fullQuery = `${
      currentPage > 1 ? `&page=${currentPage}` : ''
    }${sortByQuery}${filterQueryString}${otherQuery}`;

    const queryString = fullQuery ? `?${fullQuery}` : '';

    router.replace(`/${fullUrlSlug ?? urlSlug}${queryString}`, undefined, {
      shallow: true,
      locale,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterQueryString, sortBy]);

  useEffect(() => {
    updatePageURL();
  }, [updatePageURL]);

  const handlePaginationChange = (
    _event: ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const onSortByChange = (selectedValue: string) => {
    setSortBy(selectedValue as SearchSortByEnum);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (totalPageCount === 0) {
      return;
    }

    if (totalPageCount < currentPage && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [currentPage, setCurrentPage, totalPageCount]);

  if (appContainerId) {
    return (
      <Grid container data-testid="topic-page">
        <Grid item xs={12}>
          <DynamicBreadcrumbs pageId={id} breadcrumbsCount={breadcrumbsCount} />
        </Grid>

        <Grid item xs={12}>
          <SearchResultsHeader>
            <PageContentContainer>
              <Typography
                component="h2"
                variant="h4"
                sx={{
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </Typography>
              <Typography component="h1" variant="h3">
                {title}
              </Typography>
              <Box mt={3}>
                {displaySearchBar ? (
                  <SearchControls
                    handleSearch={handleSearchClick}
                    overrides={{
                      placeholderText: t('search:placeholder'),
                      buttonText: t('search:searchButton'),
                    }}
                    size="medium"
                  />
                ) : (
                  <Filters />
                )}
              </Box>
            </PageContentContainer>
          </SearchResultsHeader>
          <SearchResultsContainer>
            {error && <Alert severity="error">{t('searchResultsError')}</Alert>}

            <PageContentContainer>
              {appliedFilters &&
                appliedFilters.filter((a) => a.selectedOptions.length > 0)
                  ?.length > 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        [theme.breakpoints.down('sm')]: {
                          padding: `0 ${theme.spacing(2)}`,
                        },
                      }}
                      mb={2.5}
                    >
                      <AppliedFilters />
                    </Box>
                  </Grid>
                )}
              <Grid container item spacing={3} justifyContent="space-between">
                <Grid item>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: `${theme.spacing(0.5)}`,
                      [theme.breakpoints.down('sm')]: {
                        padding: `0 ${theme.spacing(2)}`,
                      },
                    }}
                  >
                    <Typography color="primary" fontWeight={700}>
                      {appCount}
                    </Typography>
                    <Typography fontWeight={500}>
                      {t('search:searchResultsCount', {
                        count: appCount,
                      })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: `${theme.spacing(0.5)}`,
                      [theme.breakpoints.down('sm')]: {
                        padding: `0 ${theme.spacing(2)}`,
                      },
                    }}
                  >
                    <SortBy
                      handleSelectedChange={onSortByChange}
                      value={sortBy}
                      isRelevanceDisabled
                      label={t('search:sortBy.label')}
                      options={sortByOptions}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex' }}>
                      <CircularProgress
                        color="primary"
                        size="6rem"
                        sx={{ margin: '0 auto' }}
                      />
                    </Box>
                  ) : (
                    <CardList>
                      {searchResults?.apps?.map((appData) => (
                        <AppCard key={appData.appId} appData={appData} />
                      ))}
                    </CardList>
                  )}
                </Grid>
              </Grid>
            </PageContentContainer>

            {totalPageCount > 1 && searchResults && (
              <CardActions
                sx={{
                  padding: `${theme.spacing(6.25)} ${theme.spacing(
                    0
                  )} ${theme.spacing(0)}`,
                  display: 'flex',
                }}
              >
                <Pagination
                  data-testid="topic-pagination"
                  sx={{ margin: 'auto' }}
                  count={totalPageCount}
                  page={currentPage}
                  onChange={handlePaginationChange}
                  disabled={totalPageCount < 2}
                />
              </CardActions>
            )}
          </SearchResultsContainer>
        </Grid>
      </Grid>
    );
  }

  return <Error statusCode={404} />;
};

export const TopicPageWithContext = (props: TopicPageProps) => {
  const { searchConfig } = useSiteContext();
  const router = useRouter();
  const {
    sortBy,
    page,
    healthCategories,
    costTypes,
    intendedAudiences,
    generalFeatures,
    clinicalFeatures,
    accreditationTypes,
    platforms,
    countryOfOrigins,
    commissionableStatuses,
    isCommissioned,
    urlSlug,
    ...rest
  } = router.query;

  const filtersQuery = {
    healthCategories,
    costTypes,
    intendedAudiences,
    generalFeatures,
    clinicalFeatures,
    accreditationTypes,
    platforms,
    countryOfOrigins,
    commissionableStatuses,
    isCommissioned,
  };

  const appliedFilters = Object.keys(filtersQuery)?.map((key: string) => {
    const { [key as keyof typeof filtersQuery]: matchingQuery } = filtersQuery;
    const queryValue = matchingQuery ?? [];
    const queryValueArray = Array.isArray(queryValue)
      ? queryValue
      : [queryValue];
    return { filterName: key, selectedOptions: queryValueArray };
  });

  let sortByQuery = sortBy
    ? enumFromStringValue<SearchSortByEnum>(SearchSortByEnum, sortBy.toString())
    : undefined;

  if (!sortByQuery) {
    const configuredDefaultSortBy = searchConfig?.orderByOptions?.find(
      (option) => option.isDefault
    );

    sortByQuery = configuredDefaultSortBy
      ? enumFromStringValue<SearchSortByEnum>(
          SearchSortByEnum,
          configuredDefaultSortBy.optionName
        )
      : SearchSortByEnum.SCORE_DESCENDING;
  }

  const pageQuery = page ? Number(page.toString()) : undefined;
  const updatedProps: TopicPageProps = {
    queryParams: { ...rest },
    ...props,
  };
  return (
    <SearchProvider
      initialSortBy={sortByQuery}
      initialPage={pageQuery}
      initialAppliedFilters={appliedFilters}
    >
      <TopicPage {...updatedProps} />
    </SearchProvider>
  );
};

TopicPageWithContext.getLayout = (
  page: ReactElement,
  _t: Translate,
  pageTitle?: string
) => {
  return <RootWidgetLayout pageTitle={pageTitle}>{page}</RootWidgetLayout>;
};
